import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiCaretDownBold,
    PiCaretRightBold,
    PiCheckCircleBold,
    PiWarningBold,
} from 'react-icons/pi';
import { PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import { listBadgeClass, ownerAvatarColor, ownerInitials } from '@v2/features/ploomes-deals/utils';

type BucketProduct = {
    product_id: number | null;
    name: string;
    code: string | null;
    quantity: number;
    amount: number;
    supplier: string | null;
};

type BucketDeal = {
    deal_id: number;
    url_key?: string | null;
    internal_number: string | null;
    display_sale_number?: string | null;
    sale_numbers?: string[] | null;
    ploomes_order_numbers?: Array<string | number> | null;
    deal_number: string | null;
    deal_title: string | null;
    finish_date: string | null;
    deal_amount: number;
    bucket_amount: number;
    products: BucketProduct[];
};

type BucketBlock = {
    bucket: string;
    label: string;
    color: string;
    weight: number;
    target: number;
    achieved: number;
    pct: number;
    products_count: number;
    deals: BucketDeal[];
};

type BucketAnalysis = {
    user: {
        id: number;
        name: string;
        email: string | null;
        avatar_url: string | null;
    };
    period: string;
    global_target: number;
    fixed_salary: number;
    performance: {
        global_achieved: number;
        pct_global: number;
        reached_100: boolean;
        earns_bonus: boolean;
        bonus_amount: number;
        is_only_force_cardio: boolean;
    };
    buckets: BucketBlock[];
};

const BUCKET_RULES: Record<string, string> = {
    force: 'REALLEADER (exceto Gluteleader e esteira/escada)',
    cardio: 'SPIRIT · REALLEADER esteiras e escadas',
    gluteleader: 'REALLEADER GLUTELEADER',
    accessories: 'OKPRO · NEWFIELD',
};

const BUCKET_ACCENT: Record<string, string> = {
    force: 'border-blue-200 bg-blue-50/50',
    cardio: 'border-red-200 bg-red-50/50',
    gluteleader: 'border-purple-200 bg-purple-50/50',
    accessories: 'border-green-200 bg-green-50/50',
};

const BUCKET_BAR: Record<string, string> = {
    force: 'bg-blue-500',
    cardio: 'bg-red-500',
    gluteleader: 'bg-purple-500',
    accessories: 'bg-green-500',
};

const BUCKET_BAR_SOFT: Record<string, string> = {
    force: 'bg-blue-200',
    cardio: 'bg-red-200',
    gluteleader: 'bg-purple-200',
    accessories: 'bg-green-200',
};

const BUCKET_DOT: Record<string, string> = {
    force: 'bg-blue-500',
    cardio: 'bg-red-500',
    gluteleader: 'bg-purple-500',
    accessories: 'bg-green-500',
};

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
}

function fmtPct(v: number, digits = 1) {
    return `${(v * 100).toFixed(digits)}%`;
}

function fmtDate(value: string | null) {
    if (!value) return '—';
    return new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR');
}

function dealSaleNumbers(deal: BucketDeal): string[] {
    if (deal.sale_numbers && deal.sale_numbers.length > 0) {
        return deal.sale_numbers.map(String);
    }
    if (deal.ploomes_order_numbers && deal.ploomes_order_numbers.length > 0) {
        return deal.ploomes_order_numbers.map(String);
    }

    return [
        String(
            deal.display_sale_number ||
                (Number(deal.deal_amount) > 0
                    ? deal.internal_number || deal.deal_number || `#${deal.deal_id}`
                    : deal.deal_number || `#${deal.deal_id}`),
        ),
    ];
}

function dealLabel(deal: BucketDeal) {
    return dealSaleNumbers(deal).join(' · ');
}

function DealSaleNumbers({ deal }: { deal: BucketDeal }) {
    const nums = dealSaleNumbers(deal);

    return (
        <span className="inline-flex flex-wrap gap-1">
            {nums.map((n) => (
                <span key={n} className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', listBadgeClass('primary'))}>
                    {n}
                </span>
            ))}
        </span>
    );
}

function periodLabel(period: string) {
    const [year, month] = period.split('-').map(Number);
    const d = new Date(year, (month || 1) - 1, 1);
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function periodOptions() {
    const opts = [];
    const now = new Date();
    for (let i = -6; i <= 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        opts.push({ val, label: periodLabel(val) });
    }
    return opts;
}

type PillTone = 'emerald' | 'amber' | 'rose' | 'sky' | 'slate' | 'violet' | 'orange';

const PILL_TONES: Record<PillTone, string> = {
    emerald: 'bg-emerald-600 text-white',
    amber: 'bg-amber-500 text-white',
    rose: 'bg-rose-600 text-white',
    sky: 'bg-sky-600 text-white',
    slate: 'bg-slate-600 text-white',
    violet: 'bg-violet-600 text-white',
    orange: 'bg-orange-500 text-white',
};

function Pill({
    children,
    tone = 'slate',
    className,
}: {
    children: ReactNode;
    tone?: PillTone;
    className?: string;
}) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                PILL_TONES[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

function clampPct(value: number) {
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.min(value, 1);
}

function ProgressBar({
    value,
    barClass,
    trackClass = 'bg-white/80',
    height = 'h-2.5',
}: {
    value: number;
    barClass: string;
    trackClass?: string;
    height?: string;
}) {
    const width = clampPct(value) * 100;

    return (
        <div className={cn('w-full overflow-hidden rounded-full', trackClass, height)}>
            <div
                className={cn('h-full rounded-full transition-all duration-500', barClass)}
                style={{ width: `${width}%` }}
            />
        </div>
    );
}

function StackedBucketBar({
    bucket,
    deals,
}: {
    bucket: BucketBlock;
    deals: BucketDeal[];
}) {
    const target = bucket.target > 0 ? bucket.target : bucket.achieved || 1;
    const soft = BUCKET_BAR_SOFT[bucket.bucket] ?? 'bg-gray-200';
    const hard = BUCKET_BAR[bucket.bucket] ?? 'bg-gray-500';

    // Segmentos empilhados: cada deal contribui % da meta do bucket
    const segments = deals.map((deal, index) => ({
        key: deal.deal_id,
        widthPct: (deal.bucket_amount / target) * 100,
        className: index % 2 === 0 ? hard : soft,
        title: `${dealLabel(deal)} · ${fmt(deal.bucket_amount)} (${fmtPct(deal.bucket_amount / target)})`,
    }));

    const totalWidth = Math.min(
        100,
        segments.reduce((sum, s) => sum + s.widthPct, 0),
    );
    const overflow = bucket.pct > 1;

    return (
        <div className="space-y-1.5">
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/80 ring-1 ring-black/5">
                <div className="absolute inset-y-0 left-0 flex" style={{ width: `${Math.min(totalWidth, 100)}%` }}>
                    {segments.map((segment) => (
                        <div
                            key={segment.key}
                            title={segment.title}
                            className={cn('h-full first:rounded-l-full last:rounded-r-full', segment.className)}
                            style={{ width: `${(segment.widthPct / Math.max(totalWidth, 0.0001)) * 100}%` }}
                        />
                    ))}
                </div>
                {overflow && (
                    <div
                        className="absolute inset-y-0 right-0 w-1.5 bg-amber-400"
                        title={`Acima da meta: ${fmtPct(bucket.pct)}`}
                    />
                )}
            </div>
            <div className="flex items-center justify-between gap-2 text-[10px] text-gray-500">
                <span>{fmt(bucket.achieved)} de {fmt(bucket.target)}</span>
                <span className="font-semibold text-gray-700">{fmtPct(bucket.pct, 0)}</span>
            </div>
        </div>
    );
}

function BucketStatusBadge({ pct }: { pct: number }) {
    if (pct >= 1) {
        return (
            <Pill tone="emerald">
                <PiCheckCircleBold className="h-3 w-3" /> Meta ok
            </Pill>
        );
    }
    if (pct <= 0) {
        return <Pill tone="slate">Zerado</Pill>;
    }
    if (pct >= 0.7) {
        return <Pill tone="amber">Quase lá</Pill>;
    }
    return (
        <Pill tone="rose">
            <PiWarningBold className="h-3 w-3" /> Em falta
        </Pill>
    );
}

function DealCard({
    deal,
    bucketTarget,
    bucketKey,
    rank,
}: {
    deal: BucketDeal;
    bucketTarget: number;
    bucketKey: string;
    rank: number;
}) {
    const [open, setOpen] = useState(false);
    const contribution = bucketTarget > 0 ? deal.bucket_amount / bucketTarget : 0;
    const shareOfBucket = contribution;

    return (
        <article className="rounded-xl border border-white/80 bg-white shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full flex-col gap-2 px-3 py-2.5 text-left"
            >
                <div className="flex items-start gap-2">
                    <span className="mt-0.5 text-gray-400">
                        {open ? <PiCaretDownBold className="h-3.5 w-3.5" /> : <PiCaretRightBold className="h-3.5 w-3.5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <DealSaleNumbers deal={deal} />
                            {rank === 1 && (
                                <Pill tone="amber">Top</Pill>
                            )}
                            <Pill tone="sky">+{fmtPct(shareOfBucket, shareOfBucket >= 0.1 ? 0 : 1)} meta</Pill>
                        </div>
                        {deal.deal_title && (
                            <p className="truncate text-[11px] text-gray-500">{deal.deal_title}</p>
                        )}
                        <p className="mt-0.5 text-[10px] text-gray-400">
                            {fmtDate(deal.finish_date)} · {deal.products.length} prod.
                        </p>
                    </div>
                    <p className="shrink-0 text-xs font-bold tabular-nums text-gray-900">{fmt(deal.bucket_amount)}</p>
                </div>

                <div className="pl-5">
                    <ProgressBar
                        value={shareOfBucket}
                        barClass={BUCKET_BAR[bucketKey] ?? 'bg-gray-500'}
                        trackClass="bg-gray-100"
                        height="h-1.5"
                    />
                    <p className="mt-1 text-[10px] text-gray-400">
                        Preenche {fmtPct(shareOfBucket)} da barra do bucket
                    </p>
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-100 px-3 py-2.5">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <Link
                            to={ploomesDealPath({
                                id: deal.deal_id,
                                url_key: deal.url_key,
                                internal_number: deal.display_sale_number || deal.internal_number,
                            })}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            Abrir deal
                            <PiArrowSquareOutBold className="h-3 w-3" />
                        </Link>
                        <div className="flex flex-wrap gap-1">
                            <Pill tone="slate">Venda {fmt(deal.deal_amount)}</Pill>
                            <Pill tone="violet">{deal.products.length} produtos</Pill>
                        </div>
                    </div>

                    {deal.products.length === 0 ? (
                        <p className="text-xs text-gray-400">Sem produtos neste bucket.</p>
                    ) : (
                        <ul className="space-y-1.5">
                            {deal.products.map((product, idx) => (
                                <li
                                    key={`${deal.deal_id}-${product.product_id ?? product.name}-${idx}`}
                                    className="rounded-lg bg-gray-50 px-2 py-1.5"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            {product.product_id ? (
                                                <Link
                                                    to={`/products/${product.product_id}`}
                                                    className="block truncate text-xs font-medium text-indigo-700 hover:underline"
                                                >
                                                    {product.name}
                                                </Link>
                                            ) : (
                                                <span className="block truncate text-xs font-medium text-gray-800">
                                                    {product.name}
                                                </span>
                                            )}
                                            <div className="mt-0.5 flex flex-wrap gap-1">
                                                {product.supplier && (
                                                    <Pill tone="slate">{product.supplier}</Pill>
                                                )}
                                                {product.code && (
                                                    <Pill tone="violet">{product.code}</Pill>
                                                )}
                                                {product.quantity > 0 && (
                                                    <Pill tone="sky">qtd {product.quantity}</Pill>
                                                )}
                                            </div>
                                        </div>
                                        <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-800">
                                            {fmt(product.amount)}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </article>
    );
}

function BucketColumn({
    bucket,
    open,
    onToggle,
    focused,
}: {
    bucket: BucketBlock;
    open: boolean;
    onToggle: () => void;
    focused: boolean;
}) {
    const rankedDeals = useMemo(
        () => [...bucket.deals].sort((a, b) => b.bucket_amount - a.bucket_amount),
        [bucket.deals],
    );

    return (
        <section
            id={`bucket-${bucket.bucket}`}
            className={cn(
                'flex min-h-0 flex-col scroll-mt-24 rounded-2xl border',
                BUCKET_ACCENT[bucket.bucket] ?? 'border-gray-200 bg-white',
                focused && 'ring-2 ring-orange-400',
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full flex-col gap-2.5 px-3 py-3 text-left"
            >
                <div className="flex items-start gap-2">
                    <span className={cn('mt-1.5 size-2.5 shrink-0 rounded-full', BUCKET_DOT[bucket.bucket] ?? 'bg-gray-400')} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-sm font-bold text-gray-900">{bucket.label}</h2>
                            <span className="text-gray-400">
                                {open ? <PiCaretDownBold className="h-4 w-4" /> : <PiCaretRightBold className="h-4 w-4" />}
                            </span>
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-gray-500">
                            {BUCKET_RULES[bucket.bucket]}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1">
                    <BucketStatusBadge pct={bucket.pct} />
                    <Pill tone="slate">{bucket.deals.length} deal(s)</Pill>
                    <Pill tone="violet">{bucket.products_count} prod.</Pill>
                    <Pill tone="sky">peso {(bucket.weight * 100).toFixed(0)}%</Pill>
                </div>

                <StackedBucketBar bucket={bucket} deals={rankedDeals} />
            </button>

            {open && (
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto border-t border-black/5 px-3 py-3">
                    {rankedDeals.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-gray-300 bg-white/70 px-3 py-6 text-center text-xs text-gray-500">
                            Nenhum deal neste bucket.
                        </p>
                    ) : (
                        rankedDeals.map((deal, index) => (
                            <DealCard
                                key={`${bucket.bucket}-${deal.deal_id}`}
                                deal={deal}
                                bucketTarget={bucket.target}
                                bucketKey={bucket.bucket}
                                rank={index + 1}
                            />
                        ))
                    )}
                </div>
            )}
        </section>
    );
}

export default function CommissionGoalBucketAnalysisPage() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const periods = periodOptions();
    const period = searchParams.get('period') || new Date().toISOString().slice(0, 7);
    const focusBucket = searchParams.get('bucket');
    const [openBuckets, setOpenBuckets] = useState<Record<string, boolean>>({});

    const { data, isLoading, isError } = useQuery<{ data: BucketAnalysis }>({
        queryKey: ['commissioning', 'goals', 'bucket-analysis', userId, period],
        queryFn: () =>
            api
                .get(`/commissioning/goals/${userId}/bucket-analysis`, { params: { period } })
                .then((r) => r.data),
        enabled: !!userId,
    });

    const analysis = data?.data;

    const initialOpen = useMemo(() => {
        if (!analysis) return {};
        const next: Record<string, boolean> = {};
        for (const bucket of analysis.buckets) {
            next[bucket.bucket] = focusBucket
                ? bucket.bucket === focusBucket
                : bucket.deals.length > 0;
        }
        return next;
    }, [analysis, focusBucket]);

    useEffect(() => {
        setOpenBuckets(initialOpen);
    }, [initialOpen]);

    useEffect(() => {
        if (!analysis || !focusBucket) return;
        const el = document.getElementById(`bucket-${focusBucket}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [analysis, focusBucket]);

    function toggleBucket(key: string) {
        setOpenBuckets((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    function expandAll() {
        if (!analysis) return;
        const next: Record<string, boolean> = {};
        for (const bucket of analysis.buckets) next[bucket.bucket] = true;
        setOpenBuckets(next);
    }

    function collapseAll() {
        if (!analysis) return;
        const next: Record<string, boolean> = {};
        for (const bucket of analysis.buckets) next[bucket.bucket] = false;
        setOpenBuckets(next);
    }

    function changePeriod(nextPeriod: string) {
        const params = new URLSearchParams(searchParams);
        params.set('period', nextPeriod);
        setSearchParams(params, { replace: true });
        setOpenBuckets({});
    }

    return (
        <div className="space-y-5">
            <PageHeader
                title="Análise por bucket"
                subtitle="Deals e produtos que compõem cada pizza da meta"
            >
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Período</span>
                        <select
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none dark:border-zinc-600 dark:bg-zinc-900"
                            value={period}
                            onChange={(e) => changePeriod(e.target.value)}
                        >
                            {periods.map((p) => (
                                <option key={p.val} value={p.val}>{p.label}</option>
                            ))}
                        </select>
                    </label>
                    <Link
                        to="/commissioning/goals"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:border-orange-300 hover:text-orange-700"
                    >
                        <PiArrowLeftBold className="h-4 w-4" />
                        Voltar às metas
                    </Link>
                </div>
            </PageHeader>

            {isLoading && <LoadingState variant="detail" />}

            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Meta não encontrada para este vendedor e período.
                    <button
                        type="button"
                        className="ml-2 font-semibold underline"
                        onClick={() => navigate('/commissioning/goals')}
                    >
                        Voltar
                    </button>
                </div>
            )}

            {analysis && (
                <>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex flex-col sm:flex-row">
                            {/* Avatar ocupa a altura do card, alinhado à esquerda */}
                            <div className="relative w-full shrink-0 sm:w-36 md:w-44">
                                {analysis.user.avatar_url ? (
                                    <img
                                        src={analysis.user.avatar_url}
                                        alt={analysis.user.name}
                                        className="h-40 w-full object-cover sm:absolute sm:inset-0 sm:h-full"
                                    />
                                ) : (
                                    <div
                                        className="flex h-40 w-full items-center justify-center text-4xl font-black text-white sm:absolute sm:inset-0 sm:h-full"
                                        style={{ backgroundColor: ownerAvatarColor(analysis.user.name) }}
                                    >
                                        {ownerInitials(analysis.user.name)}
                                    </div>
                                )}
                            </div>

                            <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 space-y-2">
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100">
                                                {analysis.user.name}
                                            </h2>
                                            {analysis.user.email && (
                                                <p className="text-xs text-gray-500">{analysis.user.email}</p>
                                            )}
                                        </div>

                                        <div className="flex flex-wrap gap-1.5">
                                            <Pill tone={analysis.performance.reached_100 ? 'emerald' : 'orange'}>
                                                Global {fmtPct(analysis.performance.pct_global)}
                                            </Pill>
                                            {analysis.performance.earns_bonus ? (
                                                <Pill tone="emerald">Super gatilho</Pill>
                                            ) : analysis.performance.is_only_force_cardio ? (
                                                <Pill tone="rose">Só Força+Cardio</Pill>
                                            ) : (
                                                <Pill tone="slate">Sem bônus ainda</Pill>
                                            )}
                                            <Pill tone="violet">{periodLabel(analysis.period)}</Pill>
                                            <Pill tone="sky">Meta {fmt(analysis.global_target)}</Pill>
                                            <Pill tone="slate">Fixo {fmt(analysis.fixed_salary)}</Pill>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={expandAll}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-orange-300 hover:text-orange-700"
                                        >
                                            Expandir tudo
                                        </button>
                                        <button
                                            type="button"
                                            onClick={collapseAll}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-orange-300 hover:text-orange-700"
                                        >
                                            Recolher tudo
                                        </button>
                                        <Link
                                            to={`/commissioning/sellers/${analysis.user.id}`}
                                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-orange-300 hover:text-orange-700"
                                        >
                                            Perfil do vendedor
                                            <PiArrowSquareOutBold className="h-3.5 w-3.5" />
                                        </Link>
                                    </div>
                                </div>

                                <div className="max-w-xl">
                                    <ProgressBar
                                        value={analysis.performance.pct_global}
                                        barClass={analysis.performance.reached_100 ? 'bg-emerald-500' : 'bg-orange-500'}
                                        trackClass="bg-gray-100 dark:bg-zinc-800"
                                        height="h-3"
                                    />
                                    <p className="mt-1.5 text-xs text-gray-500">
                                        Realizado <strong className="text-gray-800 dark:text-zinc-200">{fmt(analysis.performance.global_achieved)}</strong>
                                        {' '}de {fmt(analysis.global_target)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:items-start">
                        {analysis.buckets.map((bucket) => (
                            <BucketColumn
                                key={bucket.bucket}
                                bucket={bucket}
                                open={openBuckets[bucket.bucket] ?? false}
                                onToggle={() => toggleBucket(bucket.bucket)}
                                focused={focusBucket === bucket.bucket}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
